import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/axios";

export default function OrderProduct() {
  const { user, loading, isQRSession, refreshUser } = useAuth();
  const { slug } = useParams();
  const navigate  = useNavigate();

  const [product, setProduct]               = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [variantId, setVariantId]           = useState(null);
  const [quantity, setQuantity]             = useState(1);
  const [desiredDate, setDesiredDate]       = useState("");
  const [notes, setNotes]                   = useState("");
  const [couponInput, setCouponInput]       = useState("");
  const [couponStatus, setCouponStatus]     = useState(null);
  const [couponLoading, setCouponLoading]   = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [success, setSuccess]               = useState(null);
  const [error, setError]                   = useState("");

  // Hard guard — prevents duplicate API calls regardless of what
  // triggers a re-render or double-invocation (React strict mode,
  // double-click, network retry, etc.)
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        if (data.variants.length === 1) setVariantId(data.variants[0].id);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoadingProduct(false));
  }, [slug]);

  // Re-validate coupon whenever variant or quantity changes
  useEffect(() => {
    if (couponStatus?.valid && variantId) {
      handleValidateCoupon(couponStatus.code);
    }
  }, [variantId, quantity]);

  if (loading || loadingProduct) return <Spinner />;
  if (!user) return null;

  if (!product) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
                      text-center border border-farm-tan/30
                      dark:border-gray-700">
        <div className="text-5xl mb-4">🚫</div>
        <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
          Product Not Available
        </h2>
        <Link
          to="/products"
          className="text-farm-green dark:text-green-400 font-semibold
                     hover:underline mt-3 block"
        >
          ← Back to products
        </Link>
      </div>
    );
  }

  const selectedVariant = product.variants.find((v) => v.id === variantId);
  const subtotal   = selectedVariant
    ? parseFloat((selectedVariant.price * quantity).toFixed(2))
    : 0;
  const discount   = couponStatus?.valid ? (couponStatus.discount ?? 0) : 0;
  const finalCost  = couponStatus?.valid
    ? (couponStatus.finalCost ?? subtotal)
    : subtotal;
  const canAfford  = selectedVariant ? user.balance >= finalCost : true;
  const today      = new Date().toISOString().split("T")[0];

  // ── Coupon validation ──────────────────────────────────────────────────
  const handleValidateCoupon = async (codeOverride = null) => {
    const code = (codeOverride ?? couponInput).trim().toUpperCase();
    if (!code) return;
    if (!variantId) {
      setCouponStatus({
        valid: false,
        error: "Please select an option before applying a coupon.",
      });
      return;
    }
    setCouponLoading(true);
    try {
      const { data } = await api.get("/coupons/validate", {
        params: { code, variantId, quantity },
      });
      setCouponStatus({ ...data, error: null });
    } catch (err) {
      const errData = err.response?.data ?? {};
      setCouponStatus({
        valid:                   false,
        error:                   errData.error || "Invalid coupon code.",
        restrictedToProductName: errData.restrictedToProductName ?? null,
      });
    } finally {
      setCouponLoading(false);
    }
  };

  // ── Order submission ───────────────────────────────────────────────────
  const handleOrder = async (e) => {
    e.preventDefault();

    // If a request is already in-flight, silently ignore this call.
    // This catches React strict-mode double-invocations, double-clicks,
    // and any network-level retries that could cause duplicate emails.
    if (submittingRef.current) return;

    if (!variantId)   return setError("Please select an option.");
    if (!desiredDate) return setError("Please select a desired pickup date.");

    submittingRef.current = true;
    setError("");
    setSubmitting(true);

    try {
      const { data } = await api.post("/orders", {
        variantId,
        quantity,
        notes,
        desiredDate,
        couponCode: couponStatus?.valid ? couponStatus.code : undefined,
      });
      await refreshUser();
      setSuccess(data);
    } catch (err) {
      if (err.response?.status === 402) {
        setError(
          `Insufficient credits. You need ` +
          `${err.response.data.required} credits but have ` +
          `${err.response.data.balance?.toFixed(2)}.`
        );
      } else {
        setError(err.response?.data?.error || "Order failed.");
      }
      // Reset the ref only on error so another attempt is possible.
      // On success we navigate away so it does not matter.
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
                      text-center border border-farm-tan/30
                      dark:border-gray-700">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-farm-green dark:text-green-400
                       mb-2">
          Order Placed!
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-1">
          Your order for{" "}
          <strong>
            {success.order.quantity} × {success.order.productName} (
            {success.order.variantLabel})
          </strong>{" "}
          has been received.
        </p>
        {success.discount > 0 && (
          <p className="text-green-600 dark:text-green-400 text-sm mb-1">
            🎟️ Coupon saved you{" "}
            <strong>{success.discount.toFixed(2)} credits</strong>
          </p>
        )}
        <p className="text-gray-600 dark:text-gray-300 mb-2">
          Remaining balance:{" "}
          <strong className="text-farm-green dark:text-green-400">
            {success.newBalance?.toFixed(2)} credits
          </strong>
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          📧 A confirmation receipt has been sent to your email.
        </p>
        <div className="flex gap-3 justify-center">
          <Link
            to="/products"
            className="bg-farm-green text-white px-6 py-2 rounded-xl
                       hover:bg-green-700 transition-colors"
          >
            Shop More
          </Link>
          {!isQRSession && (
            <Link
              to="/account"
              className="border-2 border-farm-green text-farm-green
                         dark:border-green-400 dark:text-green-400
                         px-6 py-2 rounded-xl hover:bg-farm-green
                         hover:text-white transition-colors"
            >
              View Account
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8
                    border border-farm-tan/30 dark:border-gray-700
                    transition-colors duration-200">
      <Link
        to="/products"
        className="text-sm text-farm-green dark:text-green-400 hover:underline"
      >
        ← All products
      </Link>

      {/* Product header */}
      <div className="mt-4 mb-6">
        <div className="flex items-center gap-4 mb-4">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="h-20 w-20 object-cover rounded-2xl flex-shrink-0"
            />
          ) : (
            <div className="text-6xl flex-shrink-0">{product.emoji}</div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-farm-green
                           dark:text-green-400">
              {product.name}
            </h2>
            {/* Short description as subtitle */}
            {product.shortDescription && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {product.shortDescription}
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Balance
            </div>
            <div className="text-lg font-bold text-farm-green
                            dark:text-green-400">
              {user.balance?.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Long description — only shown on the product detail page */}
        {product.longDescription && (
          <div className="bg-farm-cream dark:bg-gray-700/50 rounded-xl p-4
                          border border-farm-tan/30 dark:border-gray-600
                          text-sm text-gray-700 dark:text-gray-300
                          leading-relaxed whitespace-pre-line">
            {product.longDescription}
          </div>
        )}
      </div>

      {/* Order error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300
                        dark:border-red-600 text-red-700 dark:text-red-300
                        rounded-lg p-3 mb-4 text-sm">
          {error}
          {error.includes("Insufficient") && !isQRSession && (
            <>
              {" "}
              <Link to="/add-credits" className="font-semibold underline">
                Add credits →
              </Link>
            </>
          )}
        </div>
      )}

      <form onSubmit={handleOrder} className="space-y-6">

        {/* Variant selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-3">
            Select Option
          </label>
          <div className="grid gap-3">
            {product.variants.map((v) => {
              const selected = variantId === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => { setVariantId(v.id); setError(""); }}
                  className={`
                    flex items-center justify-between p-4 rounded-xl
                    border-2 transition-all text-left
                    ${selected
                      ? "border-farm-green bg-green-50 dark:bg-green-900/30 dark:border-green-500"
                      : "border-gray-200 dark:border-gray-600 hover:border-farm-tan bg-white dark:bg-gray-700"
                    }
                  `}
                >
                  <span className="font-semibold text-gray-800
                                   dark:text-gray-100">
                    {v.label}
                  </span>
                  <span className="font-bold text-farm-green
                                   dark:text-green-400">
                    {v.price} credits
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Quantity */}
        <div>
          <label className="block text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-1">
            Quantity
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="h-10 w-10 rounded-xl border-2 border-gray-300
                         dark:border-gray-600 text-lg font-bold
                         text-gray-700 dark:text-gray-300
                         hover:border-farm-green transition-colors"
            >
              −
            </button>
            <span className="text-xl font-bold w-10 text-center
                             text-gray-800 dark:text-gray-100">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => setQuantity(Math.min(20, quantity + 1))}
              className="h-10 w-10 rounded-xl border-2 border-gray-300
                         dark:border-gray-600 text-lg font-bold
                         text-gray-700 dark:text-gray-300
                         hover:border-farm-green transition-colors"
            >
              +
            </button>
          </div>
        </div>

        {/* Coupon code */}
        <div>
          <label className="block text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-2">
            Coupon Code{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>

          {couponStatus?.valid ? (
            /* Applied coupon badge */
            <div className="flex items-center justify-between bg-green-50
                            dark:bg-green-900/30 border border-green-300
                            dark:border-green-600 rounded-xl px-4 py-3">
              <div>
                <span className="text-green-600 dark:text-green-400
                                 font-bold text-sm">
                  🎟️ {couponStatus.code}
                </span>
                <span className="text-xs text-green-600 dark:text-green-400
                                 ml-2">
                  — saving {couponStatus.discount.toFixed(2)} credits
                </span>
                {couponStatus.restrictedToProductName && (
                  <div className="text-xs text-blue-600 dark:text-blue-400
                                  mt-0.5">
                    🎯 Valid for: {couponStatus.restrictedToProductName}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setCouponInput("");
                  setCouponStatus(null);
                }}
                className="text-xs text-gray-500 dark:text-gray-400
                           hover:text-red-500 dark:hover:text-red-400
                           underline ml-4"
              >
                Remove
              </button>
            </div>
          ) : (
            /* Coupon input */
            <div className="flex gap-2">
              <input
                type="text"
                value={couponInput}
                onChange={(e) => {
                  setCouponInput(e.target.value.toUpperCase());
                  if (couponStatus?.error) setCouponStatus(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleValidateCoupon();
                  }
                }}
                placeholder="ENTER CODE"
                maxLength={32}
                className="flex-1 border border-gray-300 dark:border-gray-600
                           rounded-xl px-3 py-2 font-mono text-sm uppercase
                           bg-white dark:bg-gray-700 text-gray-800
                           dark:text-gray-100 placeholder-gray-400
                           dark:placeholder-gray-500 focus:outline-none
                           focus:ring-2 focus:ring-farm-green"
              />
              <button
                type="button"
                disabled={!couponInput.trim() || couponLoading}
                onClick={() => handleValidateCoupon()}
                className="px-4 py-2 rounded-xl border-2 border-farm-green
                           text-farm-green dark:border-green-400
                           dark:text-green-400 font-semibold text-sm
                           hover:bg-farm-green hover:text-white
                           dark:hover:bg-green-700 transition-colors
                           disabled:opacity-50"
              >
                {couponLoading ? "…" : "Apply"}
              </button>
            </div>
          )}

          {/* Coupon error — with product restriction hint */}
          {couponStatus?.error && (
            <div className="mt-1.5 space-y-1.5">
              <p className="text-xs text-red-500 dark:text-red-400">
                ✗ {couponStatus.error}
              </p>
              {couponStatus.restrictedToProductName && (
                <div className="flex items-start gap-2 bg-blue-50
                                dark:bg-blue-900/20 border border-blue-200
                                dark:border-blue-700 rounded-lg px-3 py-2">
                  <span className="text-blue-500 flex-shrink-0 mt-0.5">
                    🎯
                  </span>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    This coupon can be used on:{" "}
                    <strong>
                      {couponStatus.restrictedToProductName}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order summary */}
        {selectedVariant && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4
                          border border-gray-200 dark:border-gray-600">
            <div className="text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-2">
              Order Summary
            </div>
            <div className="flex justify-between text-sm text-gray-600
                            dark:text-gray-400">
              <span>{quantity} × {selectedVariant.label}</span>
              <span>{subtotal.toFixed(2)} credits</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-green-600
                              dark:text-green-400">
                <span>Coupon discount</span>
                <span>− {discount.toFixed(2)} credits</span>
              </div>
            )}
            <div className="border-t border-gray-200 dark:border-gray-600
                            mt-2 pt-2 flex justify-between font-bold
                            text-gray-800 dark:text-gray-100">
              <span>Total</span>
              <span className="text-farm-green dark:text-green-400">
                {finalCost.toFixed(2)} credits
              </span>
            </div>
            {!canAfford && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                Insufficient balance for this order.
                {!isQRSession && (
                  <>
                    {" "}
                    <Link
                      to="/add-credits"
                      className="font-semibold underline"
                    >
                      Add credits →
                    </Link>
                  </>
                )}
              </p>
            )}
          </div>
        )}

        {/* Desired pickup date */}
        <div>
          <label className="block text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-1">
            Desired Pickup Date{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            required
            min={today}
            value={desiredDate}
            onChange={(e) => setDesiredDate(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600
                       rounded-lg px-3 py-2 bg-white dark:bg-gray-700
                       text-gray-800 dark:text-gray-100 focus:outline-none
                       focus:ring-2 focus:ring-farm-green"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700
                            dark:text-gray-300 mb-1">
            Notes{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Any special requests or pickup instructions…"
            className="w-full border border-gray-300 dark:border-gray-600
                       rounded-lg px-3 py-2 bg-white dark:bg-gray-700
                       text-gray-800 dark:text-gray-100 resize-none
                       focus:outline-none focus:ring-2 focus:ring-farm-green"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !variantId || !canAfford}
          className="w-full bg-farm-green text-white py-3 rounded-xl
                     font-semibold text-lg hover:bg-green-700
                     transition-colors disabled:opacity-60"
        >
          {submitting ? "Placing Order…" : "Place Order"}
        </button>
      </form>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center items-center h-48">
      <div className="animate-spin h-10 w-10 rounded-full border-4
                      border-farm-green border-t-transparent" />
    </div>
  );
}
